/**
 * launchBroadcasterGoLive — shared broadcaster "GO LIVE" orchestration.
 *
 * Web port of Flutter `_goLive` (`community_screen.dart:815-832`): confirm the
 * intent, mint a fresh live-message id (parity `Uuid().v1()`, `:829`), then hand
 * off to the permission-gated launch helper as the broadcaster.
 *
 * Both owner-side go-live entry points share this exact sequence — the
 * `SelectContentTypeSheetContent` "GO LIVE" button (opened from the user's own
 * add-content flow) and the in-line `CommunityComposer` go-live button
 * (`GoLiveButtonWidget`, `community_screen.dart:1359-1367`). Factoring it here
 * keeps the two call sites behaviorally identical (finding 7, step 3).
 *
 * This is a plain presentation utility — it does NOT call React hooks. The caller
 * (a page/sheet that already has `useLocale()`) passes the resolved `l10n`, the
 * non-null current user (the broadcaster IS the current user), and its
 * `screenKey`, exactly as Flutter threads the context through `_goLive`.
 */
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import { launchLiveStreaming } from './launchLiveStreaming';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { l10n } from '@/presentation/locale/l10n';

export interface LaunchBroadcasterGoLiveArgs {
  /** The broadcaster — always the current (authenticated) user. */
  currentUser: UserEntity;
  /** Screen key for `ConfirmSheet.show` association. */
  screenKey?: string;
  /** Resolved localizations from the caller (no hooks in this plain helper). */
  l10n: l10n;
  /**
   * Optional side-effect run AFTER the confirm is accepted and BEFORE launching —
   * e.g. the select-content-type sheet closes itself here before navigating.
   */
  onConfirmed?: () => void;
}

/**
 * Show the go-live confirm, and — on accept — mint the live message id and launch
 * the live session as the broadcaster (Flutter `_goLive`,
 * `community_screen.dart:815-832`). A declined confirm is a no-op.
 *
 * @returns the minted live-message id when a broadcast was launched, or
 *   `undefined` when the confirm was declined (nothing launched). The community
 *   caller remembers this id so it can optimistically flip its own wall tile to
 *   `isLive: false` when the broadcaster returns from the live session
 *   (`CommunityPage` → `markLiveMessageEnded`, mirroring Flutter's own-list flip
 *   in `endLiveMessage`, `community_screen.dart:653-659`). The
 *   `SelectContentTypeSheetContent` call site ignores the return value.
 */
export async function launchBroadcasterGoLive(
  args: LaunchBroadcasterGoLiveArgs,
): Promise<string | undefined> {
  const { currentUser, screenKey, l10n, onConfirmed } = args;

  const response = await ConfirmSheet.show({
    title: l10n.community.liveStreamingPage.goLiveConfirmTitle,
    confirmText: l10n.community.liveStreamingPage.goLiveConfirmDescription,
    okText: l10n.community.liveStreamingPage.goLiveConfirmPositive,
    cancelText: l10n.common.close,
    screenKey,
  });
  if (!response) {
    return undefined;
  }

  onConfirmed?.();

  const liveStreamingMessageId = generateUuidV1();
  await launchLiveStreaming({
    broadcasterFirebaseId: currentUser.firebaseId,
    broadcasterName: currentUser.name,
    liveStreamingMessageId,
    asBroadcaster: true,
    l10n,
  });
  return liveStreamingMessageId;
}
